/**
 * Shared width for the app's two large modals (Edit Subscription and Help/About).
 *
 * 1024px is not arbitrary -- it is PANWDS's own `xl` Modal preset width (see the `sizes`
 * map in `@panwds/react-ui`'s Modal), so the modals line up with a design-system step
 * rather than a hand-picked number. It is applied via the `style` prop instead of
 * `size="xl"` because that preset would also raise `maxHeight` to 92vh, and both modals
 * manage their own scroll height on `ModalBody`.
 *
 * Kept in one place so the two modals stay the same width -- they are visually compared
 * side by side, and a local literal in each file would silently drift.
 */
export const LARGE_MODAL_WIDTH = 1024;

/** Leaves a 16px gutter either side on viewports narrower than the modal. */
export const LARGE_MODAL_MAX_WIDTH = 'calc(100vw - 32px)';

export const largeModalStyle = {
    width: LARGE_MODAL_WIDTH,
    maxWidth: LARGE_MODAL_MAX_WIDTH,
};

/**
 * Border applied to every modal in the app so the dialog's edge is clearly visible.
 *
 * PANWDS's `Modal` ships `twds-border-none dark:twds-border dark:twds-border-solid
 * dark:twds-border-steel-gray-1000` on its root `Card` -- so light mode has no border at
 * all, and dark mode's `steel-gray-1000` measures just 1.17:1 against the modal's own
 * surface, i.e. effectively invisible. This replaces it with `--ssmu-modal-border`
 * (light steel-gray-400, dark steel-gray-700).
 *
 * That token is deliberately *not* `--ssmu-card-border`. The modal contains bordered cards
 * (see `cardStyles.js`), and reusing the card token made the frame the exact same colour
 * as its own contents -- 1.00:1 between them -- so the outer edge read as just another row
 * divider and the dialog still looked borderless. `--ssmu-modal-border` sits two PANWDS
 * steps brighter, giving 2.63:1 against the modal surface and 1.75:1 against the inner
 * card borders, so the frame visibly out-ranks what it holds. Exact measurements live in
 * `_theme_variables.scss` next to each value.
 *
 * Two gotchas, both of which fail silently rather than erroring:
 *
 * 1. `ssmutw-border-solid` is load-bearing. Tailwind's `border` utility only emits
 *    `border-width`; the matching `border-style: solid` normally comes from Preflight,
 *    which this app disables (`corePlugins: { preflight: false }` in `tailwind.config.js`,
 *    to avoid clashing with PANWDS's base styles). Without it `border-style` keeps its
 *    initial value of `none` and nothing paints.
 *
 * 2. `[&&]` (a Tailwind arbitrary variant that doubles the class selector) is required on
 *    all three utilities. `Modal` merges `addClassName` through `twMerge` *alongside* its
 *    own border classes, but because ours carry a different prefix (`ssmutw-*` vs
 *    `twds-*`) twMerge cannot see the conflict -- both rules ship, and the winner is
 *    decided by CSS specificity. PANWDS's `dark:twds-border-steel-gray-1000` compiles to a
 *    two-class selector (`.dark\:twds-...:is(.twds-dark *)`, specificity 0,2,0) and
 *    `index.html` sets `twds-dark` permanently, so a plain single-class override (0,1,0)
 *    always loses. Doubling matches their specificity and wins on source order, with no
 *    `!important` and no dependence on an ancestor.
 *
 * Note this is deliberately *not* scoped to dark mode with a `dark:` variant: Tailwind
 * prefixes the dark class, so `dark:ssmutw-*` would compile to `:is(.ssmutw-tw-dark *)` --
 * a class this app never sets -- making it dead CSS. The theme-awareness instead comes
 * from the `--ssmu-modal-border` custom property flipping with the theme.
 *
 * The matching scrim darkening lives in `patches/@panwds__react-ui@9.27.0.patch`, since
 * `Modal` renders its backdrop with no styling API.
 */
export const MODAL_BORDER_CLASS = [
    '[&&]:ssmutw-border',
    '[&&]:ssmutw-border-solid',
    '[&&]:ssmutw-border-ssmu-modal-border',
].join(' ');

export default largeModalStyle;
